import axios from "axios";
import { ActiveFilter } from "../pages/filter-bar/FilterBadges";
import { AuthenticationError } from "@auth0/auth0-react";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;

// Debug: This should show up in console immediately when file loads
// ===== INTERFACES BASED ON BACKEND README =====

export interface AIAgentSession {
  _id?: string;
  accountId: string;
  userId: string;
  ideaId: string;
  sessionId: string;
  vertexSessionId: string;  // Vertex AI internal session ID
  agentName: string;
  agentLocation: string;
  agentId: string;
  messageCount: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastUsedAt: Date;
}

export interface AIAgentMessage {
  _id?: string;
  accountId: string;
  sessionId: string;
  userId: string;
  ideaId: string;
  messageType: 'user' | 'agent';
  content: string;
  timestamp: Date;
  mode?: string;
  metadata?: any;  // Full Vertex AI response for debugging
}

// Chat API Request/Response interfaces
export interface ChatRequest {
  message: string;
  activeFilters: ActiveFilter[]; // Mandatory field - always send, even if empty array
  sessionId?: string;
  mode?: 'validate';
}

export interface ChatResponse {
  success: boolean;
  sessionId: string;
  response: string;
  isNewSession: boolean;
  message?: string;
}

// Message History interfaces
export interface MessageHistoryParams {
  limit?: number;
  before?: string;  // ISO timestamp for pagination
}

export interface MessageHistoryResponse {
  success: boolean;
  messages: AIAgentMessage[];
  hasMore?: boolean;
  message?: string;
}

// Active Sessions interfaces
export interface ActiveSessionInfo {
  sessionId: string;
  agentName: string;
  messageCount: number;
  isActive: boolean;
  lastUsedAt: string;
}

export interface ActiveSessionsResponse {
  success: boolean;
  ideaId: string;
  sessions: ActiveSessionInfo[];
  sessionCount: number;
  message?: string;
}

// Legacy type aliases for backward compatibility
export type AgentSession = AIAgentSession;
export type AgentMessage = AIAgentMessage;

export interface CreateSessionRequest {
  ideaId: string;
  userId: string;
}

export interface CreateSessionResponse {
  success: boolean;
  session: AgentSession;
  message?: string;
}

export interface GetSessionResponse {
  success: boolean;
  session: AgentSession;
  message?: string;
}

export interface UpdateAgentStateRequest {
  idea_id: string;
  account_id: string;
  active_filters?: Record<string, any>;
}

export interface UpdateAgentStateResponse {
  success: boolean;
  message: string;
}

export interface SendMessageRequest {
  message: string;
}

export interface SendMessageResponse {
  success: boolean;
  response: {
    message: string;
    timestamp: string;
  };
  message?: string;
}

export interface GetMessagesResponse {
  success: boolean;
  messages: AgentMessage[];
  message?: string;
}

// ===== UTILITY FUNCTIONS =====

/**
 * Generic error handler for API calls
 */
const handleApiError = <T>(error: any, fallback: T): T => {
  if (error.response) {
    const { status, data } = error.response;
    console.error('🚨 [AI Agent API] HTTP Error Response:', { status, data });
    
    return {
      ...fallback,
      message: data.message || `Server error: ${status}`
    } as T;
  } else if (error.request) {
    console.error('🌐 [AI Agent API] Network Error:', error.message);
    return {
      ...fallback,
      message: 'Network error. Please check your connection and try again.'
    } as T;
  } else {
    console.error('⚠️ [AI Agent API] Other Error:', error.message);
    return {
      ...fallback,
      message: error.message || 'An unexpected error occurred'
    } as T;
  }
};

// ===== NEW API FUNCTIONS BASED ON BACKEND README =====

/**
 * Start or continue a conversation for a specific idea
 * POST /ai-agent/ideas/:ideaId/chat
 */
export const chatWithAgent = async (
  ideaId: string,
  request: ChatRequest,
  auth0: any
): Promise<ChatResponse> => {

  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    
    const url = `${API_DOMAIN}/ai-agent/ideas/${ideaId}/chat`;
    const response = await axios.post(url, request, config);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error in chat:', error);
    return handleApiError(error, {
      success: false,
      sessionId: '',
      response: '',
      isNewSession: false,
      message: 'Failed to send message'
    });
  }
};

/**
 * Retrieve conversation history for a user-idea pair
 * GET /ai-agent/ideas/:ideaId/messages
 */
export const getMessageHistory = async (
  ideaId: string,
  sessionId: string,
  params: MessageHistoryParams = {},
  auth0: any
): Promise<MessageHistoryResponse> => {
 
  try {
    
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.before) queryParams.append('before', params.before);
    if (sessionId) queryParams.append('sessionId', sessionId);
    
    const url = `${API_DOMAIN}/ai-agent/ideas/${ideaId}/messages?${queryParams.toString()}`;
    const response = await axios.get(url);
     
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting message history:', error);
    return handleApiError(error, {
      success: false,
      messages: [],
      message: 'Failed to retrieve message history'
    });
  }
};

/**
 * Retrieve messages for a specific session
 * GET /ai-agent/sessions/:sessionId/messages
 */




export const getSessionMessages = async (
  sessionId: string,
  params: MessageHistoryParams = {},
  auth0: any
): Promise<MessageHistoryResponse> => {
  
  try {
    
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.before) queryParams.append('before', params.before);
    
    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}/messages?${queryParams.toString()}`;
    
    
    const response = await axios.get(url);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting session messages:', error);
    return handleApiError(error, {
      success: false,
      messages: [],
      message: 'Failed to retrieve session messages'
    });
  }
};

/**
 * Get the active session for a specific user-idea pair
 * GET /ai-agent/ideas/:ideaId/sessions/active
 */
export const getActiveSessions = async (
  ideaId: string,
  auth0: any
): Promise<ActiveSessionsResponse> => {
 
  try {
    
    const url = `${API_DOMAIN}/ai-agent/ideas/${ideaId}/sessions/active`;
    const response = await axios.get(url);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting active sessions:', error);
    return handleApiError(error, {
      success: false,
      ideaId,
      sessions: [],
      sessionCount: 0,
      message: 'Failed to retrieve active sessions'
    });
  }
};

/**
 * Get the past sessions for a specific idea
 * GET /ai-agent/ideas/:ideaId/sessions/active
 */
export const getPastIdeaSessions = async (
  ideaId: string,
  auth0: any
): Promise<ActiveSessionsResponse> => {
  
  try {
    
    const url = `${API_DOMAIN}/ai-agent/ideas/${ideaId}/sessions/past`;
    const response = await axios.get(url);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting active sessions:', error);
    return handleApiError(error, {
      success: false,
      ideaId,
      sessions: [],
      sessionCount: 0,
      message: 'Failed to retrieve active sessions'
    });
  }
};


export const getMessagesBySessionId = async (
  sessionId: string,
  params: MessageHistoryParams = {},
  auth0: any
): Promise<MessageHistoryResponse> => {
  try {

    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.before) queryParams.append('before', params.before);

    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}/messages?${queryParams.toString()}`;
    const response = await axios.get(url);

    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting session messages:', error);
    return handleApiError(error, {
      success: false,
      messages: [],
      message: 'Failed to retrieve session messages'
    });
  }
};

// ===== LEGACY API FUNCTIONS FOR BACKWARD COMPATIBILITY =====

// Create a new agent session
export const createAgentSession = async (
  request: CreateSessionRequest,
  auth0: any
): Promise<CreateSessionResponse> => {
  
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const url = `${API_DOMAIN}/ai-agent/sessions`;
    const response = await axios.post(url, request, config);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error creating session:', error);
    return handleApiError(error, {
      success: false,
      session: {} as AgentSession,
      message: 'Failed to create session'
    });
  }
};

// Get or create a session
export const getOrCreateAgentSession = async (
  request: CreateSessionRequest,
  auth0: any
): Promise<GetSessionResponse> => {

  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    
    const url = `${API_DOMAIN}/ai-agent/sessions/get-or-create`;
    const response = await axios.post(url, request, config);
    
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting/creating session:', error);
    return handleApiError(error, {
      success: false,
      session: {} as AgentSession,
      message: 'Failed to get or create session'
    });
  }
};

// Get a specific session
export const getAgentSession = async (
  sessionId: string,
  auth0: any
): Promise<GetSessionResponse> => {
  
  try {
    
    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}`;
    const response = await axios.get(url);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting session:', error);
    return handleApiError(error, {
      success: false,
      session: {} as AgentSession,
      message: 'Failed to get session'
    });
  }
};

// Update agent state
export const updateAgentState = async (
  sessionId: string,
  request: UpdateAgentStateRequest,
  auth0: any
): Promise<UpdateAgentStateResponse> => {
 
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}/state`;
    const response = await axios.post(url, request, config);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error updating agent state:', error);
    return handleApiError(error, {
      success: false,
      message: 'Failed to update agent state'
    });
  }
};

// Send message to agent (legacy)
export const sendAgentMessage = async (
  sessionId: string,
  request: SendMessageRequest,
  auth0: any
): Promise<SendMessageResponse> => {
  
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}/message`;
    const response = await axios.post(url, request, config);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error sending message:', error);
    return handleApiError(error, {
      success: false,
      response: { message: '', timestamp: new Date().toISOString() },
      message: 'Failed to send message'
    });
  }
};

// Get session messages (legacy)
export const getAgentSessionMessages = async (
  sessionId: string,
  auth0: any
): Promise<GetMessagesResponse> => {
  
  try {
    const url = `${API_DOMAIN}/ai-agent/sessions/${sessionId}/messages`;
    const response = await axios.get(url);
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [AI Agent API] Error getting messages:', error);
    return handleApiError(error, {
      success: false,
      messages: [],
      message: 'Failed to retrieve messages'
    });
  }
};

export const getAiAgentPrompts = async (auth0: any, type: 'idea' | 'discovery'): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/ai-agent/prompts/get?type=${type}`;

    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error fetching evidence views:", e);
    throw e;
  }
};

// ===== QUERY KEYS FOR REACT QUERY =====

export const aiAgentKeys = {
  all: ['aiAgent'] as const,
  sessions: () => [...aiAgentKeys.all, 'sessions'] as const,
  session: (sessionId: string) => [...aiAgentKeys.sessions(), sessionId] as const,
  messages: () => [...aiAgentKeys.all, 'messages'] as const,
  ideaMessages: (ideaId: string) => [...aiAgentKeys.messages(), 'idea', ideaId] as const,
  sessionMessages: (sessionId: string) => [...aiAgentKeys.messages(), 'session', sessionId] as const,
  activeSessions: (ideaId: string) => [...aiAgentKeys.sessions(), 'active', ideaId] as const,
  pastSessions: (ideaId: string) => [...aiAgentKeys.sessions(), 'past', ideaId] as const,
  prompts: () => [...aiAgentKeys.all, 'prompts'] as const
};